import React, { useContext } from "react";
import { LoginContext } from "./context/LoginContext";
import {
  Route,
  Routes,
  BrowserRouter,
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  getDashboardPath,
  hasAnyRole,
  SESSION_STATUS,
} from "./auth/session";
import { APP_ROUTES, getAllowedRoles } from "./auth/access";
import "./routes.css";

import Login from "./Pages/Login";
import Menu from "./Pages/Menu";
import CadInstituicao from "./Pages/CadInstituicao";
import CadClasse from "./Pages/CadClasse";
import CadAlunoInt from "./Pages/CadAlunoInt";
import CadAlunoExt from "./Pages/CadAlunoExt";
import CadAprovAlunoExt from "./Pages/CadAprovAlunoExt";
import CadEvento from "./Pages/CadEvento";
import CadFotografo from "./Pages/CadFotografo";
import CadProduto from "./Pages/CadProduto";
import CadVenda from "./Pages/CadVenda";
import VendaDetalhe from "./Pages/VendaDetalhe";
import Eventos from "./Pages/Eventos";
import EventoDetalhe from "./Pages/EventoDetalhe";
import Upload from "./Pages/Upload";
import AlunoPerfil from "./Pages/AlunoPerfil";
import ImageProcessingPolicy from "./Pages/ImageProcessingPolicy";
import RecognitionOperations from "./Pages/RecognitionOperations";
import PublicationOperations from "./Pages/PublicationOperations";
import SelecaoFotos from "./Pages/SelecaoFotos";

const SessionBootstrapFallback = () => (
  <main
    className="session-bootstrap"
    role="status"
    aria-busy="true"
    aria-live="polite"
  >
    <span className="session-bootstrap-spinner" aria-hidden="true" />
    <span>Carregando sessão...</span>
  </main>
);

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const ctx = useContext(LoginContext);
  const logado = ctx.Logged_in;
  const user = ctx.LoggedUser;
  const location = useLocation();

  if (ctx.sessionStatus === SESSION_STATUS.LOADING) {
    return <SessionBootstrapFallback />;
  }

  if (!logado && location.pathname !== "/") {
    return <Navigate to="/" replace />;
  }

  if (logado && (!allowedRoles.length || !hasAnyRole(user, allowedRoles))) {
    return (
      <Navigate
        to={getDashboardPath(user?.role)}
        replace
        state={{
          accessDenied: true,
          from: location.pathname,
        }}
      />
    );
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const ctx = useContext(LoginContext);

  if (ctx.sessionStatus === SESSION_STATUS.LOADING) {
    return <SessionBootstrapFallback />;
  }

  if (ctx.Logged_in) {
    return <Navigate to={getDashboardPath(ctx.LoggedUser?.role)} replace />;
  }

  return children;
};

const AccessDeniedNotice = () => {
  const location = useLocation();

  if (!location.state?.accessDenied) return null;

  return (
    <div className="route-access-warning" role="alert" aria-live="assertive">
      A rota solicitada nao esta disponivel para o seu perfil.
    </div>
  );
};

const Rotas = () => {
  return (
    <BrowserRouter>
      <AccessDeniedNotice />
      <Routes>
        <Route path={APP_ROUTES.LOGIN} element={<PublicRoute><Login /></PublicRoute>} />
        <Route path={APP_ROUTES.PUBLIC_STUDENT_REGISTRATION} element={<CadAlunoExt />} />

        <Route path={APP_ROUTES.MENU} element={<ProtectedRoute allowedRoles={getAllowedRoles("MENU")}> <Menu /> </ProtectedRoute>} />
        <Route
          path={APP_ROUTES.INSTITUTIONS}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("INSTITUTIONS")}> <CadInstituicao /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.CLASSES}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("CLASSES")}> <CadClasse /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.STUDENTS}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("STUDENTS")}> <CadAlunoInt /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.STUDENT_APPROVALS}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("STUDENT_APPROVALS")}> <CadAprovAlunoExt /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.EVENT_REGISTRATION}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("EVENT_REGISTRATION")}> <CadEvento /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.PHOTOGRAPHERS}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("PHOTOGRAPHERS")}> <CadFotografo /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.PRODUCTS}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("PRODUCTS")}> <CadProduto /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.SALES}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("SALES")}> <CadVenda /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.SALE_DETAIL}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("SALE_DETAIL")}> <VendaDetalhe /> </ProtectedRoute>}
        />
        <Route path={APP_ROUTES.EVENTS} element={<ProtectedRoute allowedRoles={getAllowedRoles("EVENTS")}> <Eventos /> </ProtectedRoute>} />
        <Route
          path={APP_ROUTES.EVENT_DETAIL}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("EVENT_DETAIL")}> <EventoDetalhe /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.UPLOAD}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("UPLOAD")}> <Upload /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.IMAGE_POLICY}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("IMAGE_POLICY")}> <ImageProcessingPolicy /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.RECOGNITION_OPERATIONS}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("RECOGNITION_OPERATIONS")}> <RecognitionOperations /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.PUBLICATION_OPERATIONS}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("PUBLICATION_OPERATIONS")}> <PublicationOperations /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.STUDENT_PROFILE}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("STUDENT_PROFILE")}> <AlunoPerfil /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.STUDENT_AREA}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("STUDENT_AREA")}> <AlunoPerfil /> </ProtectedRoute>}
        />
        <Route
          path={APP_ROUTES.PHOTO_SELECTION}
          element={<ProtectedRoute allowedRoles={getAllowedRoles("PHOTO_SELECTION")}> <SelecaoFotos /> </ProtectedRoute>}
        />
      </Routes>
    </BrowserRouter>
  );
};

export default Rotas;
